<script lang="ts">
import type { PropType } from 'vue'
import {
  computed,
  defineComponent,
  reactive,
  toRefs,
  watch,
} from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import { share } from 'pinia-shared-state'
import { initCustomization } from '@/hooks/useCustomization'
import { useEnhancer } from '@/app/enhancer'
import { initEvents } from '@/hooks/useEvents'
import { parseJson } from '@/transforms/text'
import { OutputEvent } from '@/hooks/enums/event'
import App from '@/app/index.vue'

export default defineComponent({
  name: 'Root',
  components: { App },
  props: {
    mode: {
      type: String as PropType<TRenderMode>,
      default: () => 'try_on',
    },
    demo: {
      type: [String, Boolean],
      default: () => 'false',
    },
    hide: {
      type: [String, Boolean],
      default: () => 'false',
    },
    inputvariant: {
      type: String,
      default: () => '',
    },
    showswitchmode: {
      type: [String, Boolean],
      default: () => 'true',
    },
    forcerefresh: {
      type: [String, Boolean],
      default: () => 'false',
    },
  },
  emits: Object.values(OutputEvent),
  setup(props, { emit }) {
    // ! Provide
    initCustomization()
    initEvents()

    const { storeFavorites } = useEnhancer()

    const propsState = reactive({
      defaultVariant: {} as DefinitionsType | AnyObject,
      defaultDemo: props.demo === 'true',
      defaultShowSwitchMode: props.showswitchmode === 'true',
      defaultForceRefresh: props.forcerefresh === 'true',
      defaultTryOn: props.mode !== 'view_360',
    })

    const defaultHidden = computed(() => props.hide === 'true')

    const setInitDemoMode = (isDemoMode) => {
      propsState.defaultDemo = isDemoMode
      if (isDemoMode) {
        share('currentConfig', storeFavorites, { initialize: false })
        share('isSynced', storeFavorites, { initialize: false })
      }
    }

    const setInitDefaultData = (inputVariant) => {
      // * Priority load cache first
      // const payloadVariant = getStorageDefaultVariant()
      // if (payloadVariant) {
      //   propsState.defaultVariant = payloadVariant.variant
      //   propsState.defaultTryOn = payloadVariant.isTryOn
      //   clearDefaultVariant()
      //   return
      // }

      // ? Old logic
      const parserValue = parseJson(inputVariant)
      propsState.defaultVariant = isEmpty(parserValue)
        ? {
            modelName: 'frame_claude',
            frameColor: 'clear',
            templeColor: 'titanium_silver',
            lensColor: 'clear',
            lensOptions: ['none'],
            logoFrontColor: 'white',
            logoBackColor: 'white',
            bridgeName: '',
          }
        : parserValue
    }

    watch(
      () => props.inputvariant,
      (newValue) => {
        setInitDefaultData(newValue)
      },
      {
        deep: true,
        immediate: true,
      },
    )

    watch(
      () => props.demo,
      (newValue) => {
        setInitDemoMode(newValue === 'true')
      },
      {
        immediate: true,
      },
    )

    watch(
      () => props.mode,
      (newValue) => {
        propsState.defaultTryOn = newValue !== 'view_360'
      },
      {
        immediate: true,
      },
    )

    // watch(
    //   () => props.showswitchmode,
    //   (newValue) => {
    //     propsState.defaultShowSwitchMode = newValue === 'true';
    //   },
    //   {
    //     immediate: true,
    //   }
    // );

    return {
      ...toRefs(propsState),
      defaultHidden,
      handleAddToCart(payload) {
        emit(OutputEvent.addToCart, cloneDeep(payload))
      },
      handleAddToMyList(payload) {
        emit(OutputEvent.addToMyList, cloneDeep(payload))
      },
      handleShareBySns(payload) {
        emit(OutputEvent.shareBySns, cloneDeep(payload))
      },
      handleShareByLine(payload) {
        emit(OutputEvent.shareByLine, cloneDeep(payload))
      },
      handleShowMyList(isDirty: boolean) {
        emit(OutputEvent.showMyList, { changedGlassesVariant: isDirty })
      },
      handleShowPages() {
        emit(OutputEvent.showPages)
      },
      handleShowChatbot() {
        emit(OutputEvent.showChatbot)
      },
    }
  },
})
</script>

<template>
  <App
    v-if="!defaultHidden"
    :default-variant="defaultVariant"
    :default-demo="defaultDemo"
    :default-show-switch-mode="defaultShowSwitchMode"
    :default-force-refresh="defaultForceRefresh"
    :default-try-on="defaultTryOn"
    @add-to-cart="handleAddToCart"
    @add-to-my-list="handleAddToMyList"
    @share-by-sns="handleShareBySns"
    @show-my-list="handleShowMyList"
    @share-by-line="handleShareByLine"
    @show-pages="handleShowPages"
    @show-chatbot="handleShowChatbot"
  />
</template>
